import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Button, Flex, H3, Modal, P } from "orcs-design-system";

import Restricted from "src/app/Restricted";
import ErrorNotification from "src/components/ErrorNotification";
import { updateRequisition as updateRequisitionMutation } from "src/components/EditRequisitionModal/updateRequisition.graphql";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import { inbox } from "src/inbox/inbox.graphql";

import { canCloseRequisition } from "./canCloseRequisition";

type CloseRequisitionActionProps = {
  requisitionId: string;
  state?: string | null | undefined;
  onClosed?: (() => void | Promise<void>) | undefined;
  /** Forecast badge vs Position / inbox button. */
  variant?: "badge" | "button";
};

const CloseRequisitionAction = ({
  requisitionId,
  state,
  onClosed,
  variant = "button",
}: CloseRequisitionActionProps) => {
  const { enablePositionBackedVacancies } = useWorkspaceFeatureFlags();
  const [updateRequisition] = useMutation(updateRequisitionMutation, {
    refetchQueries: [inbox],
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  if (!enablePositionBackedVacancies || !canCloseRequisition(state)) {
    return null;
  }

  const onConfirm = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await updateRequisition({
        variables: { input: { id: requisitionId, state: "CLOSED" } },
      });
      setIsOpen(false);
      await onClosed?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Restricted>
      <Button
        type="button"
        small
        variant={variant === "badge" ? "ghost" : "danger"}
        data-testid="close-requisition-button"
        onClick={() => setIsOpen(true)}
      >
        Close hiring request
      </Button>
      {isOpen && (
        <Modal
          visible={isOpen}
          onClose={() => {
            if (!isProcessing) {
              setIsOpen(false);
            }
          }}
          maxWidth="90vw"
          width={["90vw", "90vw", "480px"]}
          headerContent={<H3>Close hiring request</H3>}
          footerContent={
            <Flex gap="s">
              <Button
                variant="danger"
                onClick={() => {
                  void onConfirm();
                }}
                disabled={isProcessing}
                isLoading={isProcessing}
                data-testid="close-requisition-confirm"
              >
                Close hiring request
              </Button>
              <Button
                variant="ghost"
                onClick={() => setIsOpen(false)}
                disabled={isProcessing}
              >
                Keep open
              </Button>
            </Flex>
          }
        >
          <P my="r">
            Closing releases this request&apos;s FTE back to the position. This
            cannot be undone.
          </P>
          {error ? (
            <ErrorNotification
              message={error.message}
              error={error}
              floating={false}
            />
          ) : null}
        </Modal>
      )}
    </Restricted>
  );
};

export default CloseRequisitionAction;
